import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Link as LinkIcon, Loader2, Paperclip, Phone, User } from "lucide-react";
import useCrmApi from "../../../components/hook/crm-req";
import useRequest from "../../../components/hook/use-req";
import { TicketPriority, TicketProps, TicketStatus } from "./types";
import { TeamDetails } from "../../../components/crm/customer/types";

// This route (routes/main-routes.tsx: "ticketing/:ticketId") is registered
// outside AuthGuard — it's the link shared with a customer to check their
// own ticket, not an agent tool. Agents edit tickets from the board/drawer
// (views/crm-pages/ticket/index.tsx + components/crm/tickets/details.tsx)
// instead. Per explicit requirement, this public page is read-only end to
// end, and commenting is deferred rather than partially built here.
const STATUS_LABEL: Record<TicketStatus, string> = {
  open: "To Do",
  pending: "In Progress",
  closed: "Done",
};

const STATUS_STYLES: Record<TicketStatus, string> = {
  open: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  pending: "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  closed: "bg-slate-100 text-slate-500 dark:bg-slate-500/15 dark:text-slate-400",
};

const PRIORITY_LABEL: Record<TicketPriority, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

function isOverdue(ticket: TicketProps): boolean {
  if (!ticket || ticket.status === "closed") return false;
  const opened = new Date(ticket.opened_at).getTime();
  if (Number.isNaN(opened)) return false;
  const hoursOpen = (Date.now() - opened) / 36e5;
  return ticket.priority === "critical" || ticket.priority === "high"
    ? hoursOpen > 0
    : hoursOpen > 24;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "N/A";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// FastAPI's `detail` is a plain string for most errors but an array of
// {type, loc, msg, ctx} objects for 422 validation errors — never render it
// raw into JSX.
function extractErrorMessage(payload: any, fallback: string): string {
  const detail = payload?.detail ?? payload?.message;
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join(", ") || fallback;
  }
  if (typeof detail === "object") return detail.msg || fallback;
  return fallback;
}

const getTeamMemberName = (member?: TeamDetails | null): string =>
  member ? `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email : "";

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-xs text-slate-400 dark:text-slate-500 mb-1">{label}</p>
    {children}
  </div>
);

const SingleTicket: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Coming from the "view ticket" email link means a token rides in the URL
  // (?token=...) — the agent opening it may not have a session in this
  // browser at all, so that token is tried first. Falling back to the
  // stored session token keeps this working for an already-logged-in agent
  // who navigates here without one in the URL.
  const token = searchParams.get("token") || localStorage.getItem("authToken");

  const [ticket, setTicket] = useState<TicketProps | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [team, setTeam] = useState<TeamDetails[]>([]);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  const { makeRequest: getTicket } = useCrmApi(
    ticketId ? `/tickets/${ticketId}` : "",
    "GET",
    { Authorization: `Bearer ${token}` },
  );

  const { makeRequest: getTeamList } = useRequest("/profile/users", "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: getCurrentUser } = useRequest("/auth/get_userdata", "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    (async () => {
      if (!ticketId) {
        setLoadError("No ticket id in the URL.");
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError(null);
      try {
        const [response] = await getTicket();
        const data = response?.data || response;
        if (data?.id) {
          setTicket(data);
        } else {
          setLoadError(extractErrorMessage(response, "Ticket not found."));
        }
      } catch (err) {
        setLoadError("Failed to load ticket. Please check the link and try again.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  useEffect(() => {
    (async () => {
      try {
        const [response] = await getCurrentUser();
        if (response?.payload?.user_id) setCurrentUserId(response.payload.user_id);
      } catch {
        // non-fatal — "Self" resolution just won't be available
      }
    })();
    (async () => {
      try {
        const [response] = await getTeamList();
        if (Array.isArray(response)) setTeam(response);
        else if (Array.isArray(response?.users)) setTeam(response.users);
        else if (Array.isArray(response?.results)) setTeam(response.results);
      } catch {
        setTeam([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assignedToLabel = (): string => {
    if (!ticket?.assigned_to_user_id) return "Unassigned";
    if (currentUserId && ticket.assigned_to_user_id === currentUserId) return "Self";
    const member = team.find((m) => m.id === ticket.assigned_to_user_id);
    return getTeamMemberName(member) || "Assigned";
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard");
  };

  // ── loading / error states ────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-indigo-600 dark:text-indigo-400 animate-spin" />
          <p className="text-sm text-slate-400 dark:text-slate-500">Loading ticket…</p>
        </div>
      </div>
    );
  }

  if (loadError || !ticket) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-slate-950 px-6">
        <div className="text-center max-w-sm">
          <p className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">
            Couldn't load this ticket
          </p>
          <p className="text-sm text-slate-400 dark:text-slate-500 mb-6">
            {loadError || "Something went wrong."}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* top bar — intentionally minimal, this route renders outside the
          normal CRM sidebar/navbar (see routes/main-routes.tsx) */}
      <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-4 py-3">
        <button
          onClick={() => navigate(-1)}
          title="Back"
          className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
        >
          <ArrowLeft size={18} />
        </button>
        <button
          onClick={copyLink}
          title="Copy link"
          className="text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300"
        >
          <LinkIcon size={17} />
        </button>
      </div>

      {/* header — the synopsis (AI summary when generated, else the issue description) is the headline; the raw
          ticket id is deliberately not shown here. */}
      <div className="px-6 pt-5 pb-4 border-b border-slate-100 dark:border-slate-800">
        <div className="max-w-4xl mx-auto flex items-center gap-3 flex-wrap">
          <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {ticket.synopsis || ticket.issue_description || ticket.issue_type || "Ticket"}
          </h1>
          <span
            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[ticket.status]}`}
          >
            {STATUS_LABEL[ticket.status]}
          </span>
        </div>
      </div>

      <div className="px-6 py-6 max-w-4xl mx-auto space-y-5">
        <Field label="Issue Type">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{ticket.issue_type || "Not specified"}</p>
        </Field>

        <Field label="Assigned to">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{assignedToLabel()}</p>
        </Field>

        {/* "Assigned On" has no dedicated field in the ticket API today
            (TicketProps only has opened_at/resolved_at/created_at/updated_at)
            — falling back to updated_at as the closest available signal
            rather than fabricating a value. */}
        <Field label="Assigned On">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            {ticket.assigned_to_user_id ? formatDate(ticket.assigned_at || ticket.updated_at) : "—"}
          </p>
        </Field>

        <Field label="Status">
          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[ticket.status]}`}>
            {STATUS_LABEL[ticket.status]}
          </span>
        </Field>

        <Field label="Priority">
          {isOverdue(ticket) ? (
            <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">Overdue</span>
          ) : (
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{PRIORITY_LABEL[ticket.priority] || "Not set"}</span>
          )}
        </Field>

        <Field label="Issue Description">
          <p className="text-sm text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">
            {ticket.issue_description || "No description"}
          </p>
        </Field>

        {(ticket.customer_name || ticket.customer_phone) && (
          <Field label="Issue Reported For">
            <div className="flex items-center gap-2.5">
              <span className="w-8 h-8 shrink-0 rounded-full bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center">
                <User size={16} className="text-indigo-600 dark:text-indigo-400" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                  {ticket.customer_name || "Unknown customer"}
                </p>
                {ticket.customer_phone && (
                  <p className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                    <Phone size={11} /> {ticket.customer_phone}
                  </p>
                )}
              </div>
            </div>
          </Field>
        )}

        {ticket.tags?.length > 0 && (
          <Field label="Ticket Tags">
            <div className="flex gap-2 flex-wrap">
              {ticket.tags.map((tag) => (
                <span
                  key={tag}
                  className="bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-400 text-xs px-2 py-0.5 rounded-md"
                >
                  {tag}
                </span>
              ))}
            </div>
          </Field>
        )}

        {ticket.attachment_file_ids?.length > 0 && (
          <Field label="Attachments">
            <div className="flex flex-wrap items-center gap-2">
              {ticket.attachment_file_ids.map((fileUrl, index) => {
                const lower = fileUrl.toLowerCase();
                const isImage = /\.(jpg|jpeg|png|gif|webp)$/.test(lower);
                return (
                  <a
                    key={`${fileUrl}-${index}`}
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-20 h-20 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 shrink-0"
                  >
                    {isImage ? (
                      <img src={fileUrl} alt={`attachment-${index}`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-center p-2">
                        <Paperclip size={18} className="text-slate-400 dark:text-slate-500 mb-1" />
                        <span className="text-[10px] text-slate-500 dark:text-slate-400 break-all line-clamp-2">
                          {fileUrl.split("/").pop()?.slice(0, 15)}
                        </span>
                      </div>
                    )}
                  </a>
                );
              })}
            </div>
          </Field>
        )}
      </div>
    </div>
  );
};

export default SingleTicket;
